import { type ReactNode, useState } from "react"
import { router } from "@inertiajs/react"
import { ChevronDown, Pencil, Plus, Tags, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { MainContainer } from "@/components/ui/container"
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import { InlineSelect } from "@/components/ui/inline-select"
import { StatusBadge } from "@/components/ui/status-badge"
import { CouponList } from "@/features/staff/promotions/coupon-list"
import { PromotionForm } from "@/features/staff/promotions/promotion-form"
import { PromotionSchedule } from "@/features/staff/promotions/promotion-schedule"
import {
  PROMOTION_PATHS,
  promotionTone,
  promotionValueLabel,
  tPromotion,
  type MenuCategoryOption,
  type MenuItemOption,
  type Promotion,
  type PromotionOption,
} from "@/features/staff/promotions/promotions"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"
import { cn } from "@/lib/utils"

export type PromotionsWorkspaceProps = {
  promotions: Promotion[]
  items: MenuItemOption[]
  categories: MenuCategoryOption[]
  kinds: PromotionOption[]
  scopes: PromotionOption[]
  statuses: PromotionOption[]
  timeZone: string | null
  currency: string
  maxPercentage: number
}

type DrawerState = { mode: "create" } | { mode: "edit"; promotion: Promotion } | null

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.promotions.index"

/**
 * A campaign date is a date in the restaurant's own zone and it arrives as
 * `YYYY-MM-DD`. `new Date("2026-08-20")` parses as UTC midnight, so rendering it
 * in a negative-offset zone shows the 19th — the screen would disagree with
 * `Location::Promotion#within_dates?`, which reads the date in the restaurant's
 * zone, by exactly one day at both ends of every campaign. Reading it back in
 * UTC is what makes the label say the date that was stored.
 */
function formatCampaignDate(value: string): string {
  return formatDateTime(value, {
    timeZone: "UTC",
    options: { year: "numeric", month: "short", day: "numeric" },
  })
}

/** The campaign bounds in one phrase, including the common case of neither. */
export function campaignDatesLabel(promotion: Pick<Promotion, "startsOn" | "endsOn">): string {
  const { startsOn, endsOn } = promotion

  if (startsOn && endsOn) {
    return tPromotion("dates_range", {
      from: formatCampaignDate(startsOn),
      to: formatCampaignDate(endsOn),
    })
  }
  if (startsOn) return tPromotion("dates_from", { date: formatCampaignDate(startsOn) })
  if (endsOn) return tPromotion("dates_until", { date: formatCampaignDate(endsOn) })

  return tPromotion("dates_always")
}

/** What the rule reaches, counted rather than listed: a card is scanned. */
export function reachLabel(promotion: Pick<Promotion, "scope" | "targets">): string {
  if (promotion.scope === "items") {
    return tPromotion("reach_items", { count: promotion.targets.length })
  }
  if (promotion.scope === "categories") {
    return tPromotion("reach_categories", { count: promotion.targets.length })
  }

  return tPromotion("reach_bill")
}

function SummaryFigure({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground text-sm font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </div>
  )
}

function Section({
  title,
  testId,
  children,
}: {
  title: string
  testId: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full justify-between"
          data-testid={testId}
        >
          {title}
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">{children}</CollapsibleContent>
    </Collapsible>
  )
}

function PromotionCard({
  promotion,
  statuses,
  currency,
  timeZone,
  onEdit,
  onDelete,
}: {
  promotion: Promotion
  statuses: PromotionOption[]
  currency: string
  timeZone: string | null
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <Card density="compact" data-testid={`promotion-card-${promotion.id}`}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span className="truncate">{promotion.name}</span>
          <StatusBadge
            tone={promotionTone(promotion.status, promotion.liveNow)}
            label={
              promotion.status === "active" && !promotion.liveNow
                ? tPromotion("not_live_now")
                : promotion.statusLabel
            }
            data-testid={`promotion-status-${promotion.id}`}
          />
          {promotion.liveNow && (
            <span
              className="text-muted-foreground text-xs"
              data-testid={`promotion-live-${promotion.id}`}
            >
              {tPromotion("live_now")}
            </span>
          )}
        </CardTitle>

        <CardAction className="flex items-center gap-2">
          <InlineSelect
            value={promotion.status}
            options={statuses.map((option) => ({
              value: option.value,
              label: option.label,
              testId: `promotion-${promotion.id}-status-option-${option.value}`,
            }))}
            size="sm"
            className="w-36"
            successMessage={tPromotion("status_updated")}
            data-testid={`promotion-${promotion.id}-status`}
            onSubmit={(value) =>
              patchInline(PROMOTION_PATHS.update(promotion.id), { status: value })
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
            aria-label={tPromotion("edit")}
            data-testid={`promotion-edit-${promotion.id}`}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
            aria-label={tPromotion("delete")}
            data-testid={`promotion-delete-${promotion.id}`}
          >
            <Trash2 className="size-4" />
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryFigure
            label={promotion.kindLabel}
            value={promotionValueLabel(promotion, currency)}
            testId={`promotion-value-${promotion.id}`}
          />
          <SummaryFigure
            label={promotion.scopeLabel}
            value={reachLabel(promotion)}
            testId={`promotion-reach-${promotion.id}`}
          />
          <SummaryFigure
            label={tPromotion("starts_on_label")}
            value={campaignDatesLabel(promotion)}
            testId={`promotion-dates-${promotion.id}`}
          />
          <SummaryFigure
            label={tPromotion("priority_label")}
            value={String(promotion.priority)}
            testId={`promotion-priority-${promotion.id}`}
          />
        </div>

        <div className="border-border divide-border divide-y border-t">
          <Section
            title={tPromotion("window_open")}
            testId={`promotion-${promotion.id}-window-toggle`}
          >
            <PromotionSchedule
              promotionId={promotion.id}
              window={promotion.window}
              timeZone={promotion.timeZone ?? timeZone}
            />
          </Section>

          <Section
            title={tPromotion("coupons_open", { count: promotion.coupons.length })}
            testId={`promotion-${promotion.id}-coupons-toggle`}
          >
            <CouponList
              promotionId={promotion.id}
              coupons={promotion.coupons}
              timeZone={promotion.timeZone ?? timeZone}
            />
          </Section>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Configuring the standing price rules a restaurant applies on its own.
 *
 * ## The empty state is the screen a restaurant sees first
 *
 * Switching the `promotions` feature on gives a restaurant this page and nothing
 * else — an empty list is the normal first state, not a failure. So it says what
 * a promotion is and points at the one action that matters, rather than
 * rendering an empty table with column headers over nothing. A manager who just
 * turned a feature on and is handed a blank grid has no way to tell "you have
 * none" from "this did not load".
 *
 * ## Rules are listed in the order the till walks them
 *
 * The server orders by `priority` descending, which is exactly the order
 * `Location::PromotionEvaluationService` evaluates in. Sorting by name or by
 * recency here would show a manager something other than what the till will do
 * when two rules reach the same dish.
 *
 * ## `status` is the only inline field on this screen
 *
 * Flipping a rule between draft and active is the switch a manager reaches for
 * mid-service, and it commits on change. Everything else — kind, value, scope,
 * ceiling, dates — only means anything together, so it lives behind an explicit
 * submit: "20" saved before the kind changed from percentage to pesos is a
 * different promotion than either of the two the operator had in mind.
 */
export function PromotionsWorkspace({
  promotions,
  items,
  categories,
  kinds,
  scopes,
  statuses,
  timeZone,
  currency,
  maxPercentage,
}: PromotionsWorkspaceProps) {
  const [drawer, setDrawer] = useState<DrawerState>(null)
  const [pendingDelete, setPendingDelete] = useState<Promotion | null>(null)

  const confirmDelete = () => {
    if (!pendingDelete) return

    router.delete(PROMOTION_PATHS.destroy(pendingDelete.id), {
      preserveScroll: true,
      onFinish: () => setPendingDelete(null),
    })
  }

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={t(`${PAGE_SCOPE}.title`)}
          description={t(`${PAGE_SCOPE}.description`)}
          actions={
            promotions.length > 0 ? (
              <Button
                type="button"
                onClick={() => setDrawer({ mode: "create" })}
                data-testid="promotion-new"
              >
                <Plus className="size-4" />
                {tPromotion("new")}
              </Button>
            ) : undefined
          }
        />

        {promotions.length === 0 ? (
          <Card data-testid="promotions-empty">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <Tags className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tPromotion("empty_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">{tPromotion("empty_hint")}</p>
              <Button
                type="button"
                onClick={() => setDrawer({ mode: "create" })}
                data-testid="promotion-new-empty"
              >
                <Plus className="size-4" />
                {tPromotion("empty_action")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="promotions-list">
            {promotions.map((promotion) => (
              <PromotionCard
                key={promotion.id}
                promotion={promotion}
                statuses={statuses}
                currency={currency}
                timeZone={timeZone}
                onEdit={() => setDrawer({ mode: "edit", promotion })}
                onDelete={() => setPendingDelete(promotion)}
              />
            ))}
          </div>
        )}
      </div>

      {drawer && (
        <ResponsiveDrawer open onOpenChange={(next) => !next && setDrawer(null)}>
          <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
            <DrawerHeader>
              <DrawerTitle>
                {drawer.mode === "edit" ? tPromotion("edit_title") : tPromotion("new_title")}
              </DrawerTitle>
              <DrawerDescription>{tPromotion("form_description")}</DrawerDescription>
            </DrawerHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              <PromotionForm
                promotion={drawer.mode === "edit" ? drawer.promotion : undefined}
                kinds={kinds}
                scopes={scopes}
                items={items}
                categories={categories}
                maxPercentage={maxPercentage}
                onDone={() => setDrawer(null)}
              />
            </div>
          </DrawerContent>
        </ResponsiveDrawer>
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tPromotion("delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{tPromotion("delete_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promotion-delete-cancel">
              {tPromotion("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="promotion-delete-confirm">
              {tPromotion("delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainContainer>
  )
}
